<template>
  <page icon="exchange-alt" title="Transfer Report">

    <template #tools>
      <action-button
        @click="downloadCsv"
        :working="downloading"
        class="is-primary is-rounded"
        left-icon="download">
        {{ selectedCount ? `Export ${selectedCount} selected` : 'Download CSV' }}
      </action-button>
    </template>

    <template #default>
      <div class="box mb-1">
        <div class="grid gap-1 has-3-columns">
          <text-input
            class="is-marginless"
            v-model="filters.asset_code"
            @input="runFilters"
            :disabled="loading"
            left-icon="tag"
            :debounce="500"
            placeholder="Filter by Asset Code, Custom Fields"
            :label="false">
            <action-button
              v-if="filters.asset_code"
              :disabled="loading"
              slot="right"
              class="is-light"
              @click="clearFilter('asset_code')">
              <icon icon="times"/>
            </action-button>
          </text-input>

          <text-input
            class="is-marginless"
            v-model="filters.asset_barcode"
            @input="runFilters"
            :disabled="loading"
            left-icon="barcode"
            :debounce="500"
            placeholder="Filter by Barcode"
            :label="false">
            <action-button
              v-if="filters.asset_barcode"
              :disabled="loading"
              slot="right"
              class="is-light"
              @click="clearFilter('asset_barcode')">
              <icon icon="times"/>
            </action-button>
          </text-input>

          <date-picker
            class="is-marginless"
            placeholder="From Date"
            left-icon="calendar"
            :label="false"
            :value="filters.from_date"
            @input="val => { filters.from_date = val; runFilters() }"
            :disabled="loading">
            <action-button
              v-if="filters.from_date"
              :disabled="loading"
              slot="right"
              @click="clearFilter('from_date')">
              <icon icon="times"/>
            </action-button>
          </date-picker>

          <date-picker
            class="is-marginless"
            placeholder="To Date"
            left-icon="calendar"
            :label="false"
            :value="filters.to_date"
            @input="val => { filters.to_date = val; runFilters() }"
            :disabled="loading">
            <action-button
              v-if="filters.to_date"
              :disabled="loading"
              slot="right"
              @click="clearFilter('to_date')">
              <icon icon="times"/>
            </action-button>
          </date-picker>

          <div class="is-flex align-center" style="padding-top: 2px">
            <a
              class="button is-rounded"
              :class="filters.has_job ? 'is-primary' : 'is-light'"
              :disabled="loading"
              @click="toggleHasJob">
              <span class="icon"><icon icon="briefcase" /></span>
              <span>With jobs only</span>
            </a>
          </div>
        </div>

        <div class="mt-2">
          <transfer-report-filter-tabs
            v-model="multiFilters"
            :disabled="loading"
            @input="onMultiFilterChange" />
        </div>
      </div>

      <div class="box">
        <loader v-bind="{ loading }" loading-text="Loading transfer report...">
          <div v-if="selectedCount" class="is-flex align-center mb-2" style="gap: 8px">
            <span class="tag is-primary is-rounded">{{ selectedCount }} selected</span>
            <a class="is-size-7 has-text-grey" @click="clearSelection">Clear selection</a>
          </div>
          <pager :pageable="transfers" @nav="goToPage"/>
          <table class="table is-fullwidth is-hoverable">
              <thead>
                <tr>
                  <th style="width: 40px">
                    <input
                      type="checkbox"
                      :checked="allPageSelected"
                      :indeterminate.prop="isIndeterminate"
                      @change="toggleAll" />
                  </th>
                  <th>Asset Code / {{ Naming.AssetType }}</th>
                  <th>Technician / Transfer Date</th>
                  <th>From {{ Naming.Site }} / {{ Naming.Zone }}</th>
                  <th>From {{ Naming.Client }}</th>
                  <th>To {{ Naming.Site }} / {{ Naming.Zone }}</th>
                  <th>To {{ Naming.Client }}</th>
                  <th>Job Type / Number</th>
                  <th>Dynamic Statuses</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="transfer in transfers.data" :key="transfer.id" :class="{ 'has-background-primary-light': isSelected(transfer) }">
                  <td>
                    <input type="checkbox" :checked="isSelected(transfer)" @change="toggleItem(transfer)" />
                  </td>
                  <td>
                    <strong>
                      <router-link v-if="transfer.asset_id" :to="{ name: 'asset-overview', params: { asset: transfer.asset_id } }">{{ transfer.asset_code || '—' }}</router-link>
                      <span v-else>{{ transfer.asset_code || '—' }}</span>
                    </strong>
                    <p v-if="transfer.asset_type" class="has-text-grey">{{ transfer.asset_type }}</p>
                  </td>
                  <td>
                    <strong>
                      <router-link v-if="transfer.technician_id" :to="{ name: 'user-overview', params: { user: transfer.technician_id } }">{{ transfer.technician_name }}</router-link>
                      <span v-else>{{ transfer.technician_name || '—' }}</span>
                    </strong>
                    <p class="has-text-grey">{{ transfer.transfer_date | date('L LTS') }}</p>
                  </td>
                  <td>
                    <strong>
                      <router-link v-if="transfer.from_site_id" :to="{ name: 'site-overview', params: { site: transfer.from_site_id } }">{{ transfer.from_site_name }}</router-link>
                      <span v-else>{{ transfer.from_site_name || '—' }}</span>
                    </strong>
                    <p v-if="transfer.from_zone_name" class="has-text-grey">{{ transfer.from_zone_name }}</p>
                  </td>
                  <td>
                    <router-link v-if="transfer.from_client_id" :to="{ name: 'client-overview', params: { client: transfer.from_client_id } }">{{ transfer.from_client_name }}</router-link>
                    <span v-else>{{ transfer.from_client_name || '—' }}</span>
                  </td>
                  <td>
                    <strong>
                      <router-link v-if="transfer.to_site_id" :to="{ name: 'site-overview', params: { site: transfer.to_site_id } }">{{ transfer.to_site_name }}</router-link>
                      <span v-else>{{ transfer.to_site_name || '—' }}</span>
                    </strong>
                    <p v-if="transfer.to_zone_name" class="has-text-grey">{{ transfer.to_zone_name }}</p>
                  </td>
                  <td>
                    <router-link v-if="transfer.to_client_id" :to="{ name: 'client-overview', params: { client: transfer.to_client_id } }">{{ transfer.to_client_name }}</router-link>
                    <span v-else>{{ transfer.to_client_name || '—' }}</span>
                  </td>
                  <td>
                    <strong>{{ transfer.job_type || '—' }}</strong>
                    <p class="has-text-grey">
                      <router-link v-if="transfer.job_id && transfer.job_number" :to="{ name: 'job-handler', params: { job: transfer.job_id } }">{{ transfer.job_number }}</router-link>
                      <span v-else>{{ transfer.job_number || '—' }}</span>
                    </p>
                  </td>
                  <td>
                    <template v-if="transfer.dynamic_statuses && transfer.dynamic_statuses.length">
                      <p v-for="status in transfer.dynamic_statuses" :key="status.name" class="is-marginless">
                        <span class="has-text-weight-semibold">{{ status.name }}:</span> {{ status.value }}
                      </p>
                    </template>
                    <span v-else>—</span>
                  </td>
                </tr>
              </tbody>
            </table>
          <pager :pageable="transfers" @nav="goToPage"/>
        </loader>
      </div>
    </template>

  </page>
</template>

<script>
import { mapGetters } from 'vuex'
import { debounce } from 'lodash'
import download from 'downloadjs'
import moment from 'moment'
import { assetTransfer as backend } from '@/api'
import TransferReportFilterTabs from './TransferReportFilterTabs.vue'

export default {

  components: { TransferReportFilterTabs },

  data: () => ({
    loading: true,
    downloading: false,
    selectedItems: [],
    filters: {
      asset_code: '',
      asset_barcode: '',
      from_date: '',
      to_date: '',
      has_job: false,
      page: '',
    },
    multiFilters: {
      site_ids: [],
      zone_ids: [],
      client_ids: [],
      asset_type_ids: [],
      job_type_ids: [],
      dynamic_status_option_ids: [],
    },
  }),

  created() {
    const q = this.$route.query
    this.filters = { asset_code: q.asset_code || '', asset_barcode: q.asset_barcode || '', from_date: q.from_date || '', to_date: q.to_date || '', has_job: q.has_job === 'true', page: q.page || '' }
    this.restoreMultiFilters(q)
    this.loadReport()
  },

  methods: {
    loadReport() {
      this.loading = true
      const activeMulti = Object.fromEntries(
        Object.entries(this.multiFilters).filter(([, v]) => v.length > 0)
      )
      this.$store.dispatch('assetTransfer/loadReport', {
        path: '/api/asset-transfers/report',
        params: { ...this.$route.query, ...activeMulti },
      }).then(() => { this.loading = false })
        .catch(() => { this.loading = false })
    },

    restoreMultiFilters(query) {
      const toIntArray = v => !v ? [] : (Array.isArray(v) ? v : [v]).map(Number)
      this.multiFilters = {
        site_ids:                  toIntArray(query.site_ids),
        zone_ids:                  toIntArray(query.zone_ids),
        client_ids:                toIntArray(query.client_ids),
        asset_type_ids:            toIntArray(query.asset_type_ids),
        job_type_ids:              toIntArray(query.job_type_ids),
        dynamic_status_option_ids: toIntArray(query.dynamic_status_option_ids),
      }
    },

    onMultiFilterChange() {
      this.selectedItems = []
      if (this.multiFilters.asset_type_ids.length !== 1) {
        this.multiFilters.dynamic_status_option_ids = []
      }
      this.$nextTick(() => {
        const activeMulti = Object.fromEntries(
          Object.entries(this.multiFilters).filter(([, v]) => v.length > 0)
        )
        const cleaned = Object.fromEntries(
          Object.entries(this.filters).filter(([k, v]) => k !== 'page' && v !== null && v !== '' && v !== undefined && v !== false)
        )
        this.$router.push({ name: 'asset-transfer-report', query: { ...cleaned, ...activeMulti } })
      })
    },

    goToPage(apiUrl) {
      const url = new URL(apiUrl)
      const page = url.searchParams.get('page')
      const activeMulti = Object.fromEntries(
        Object.entries(this.multiFilters).filter(([, v]) => v.length > 0)
      )
      this.$router.push({
        name: 'asset-transfer-report',
        query: { ...this.filters, page, ...activeMulti },
      })
    },

    runFilters: debounce(function () {
      if (this.loading) return
      this.selectedItems = []
      const activeMulti = Object.fromEntries(
        Object.entries(this.multiFilters).filter(([, v]) => v.length > 0)
      )
      const cleaned = Object.fromEntries(
        Object.entries(this.filters).filter(([k, v]) => k !== 'page' && v !== null && v !== '' && v !== undefined && v !== false)
      )
      this.$router.push({ name: 'asset-transfer-report', query: { ...cleaned, ...activeMulti } })
    }, 500),

    clearFilter(key) {
      this.filters[key] = null
      this.runFilters()
    },

    toggleHasJob() {
      this.filters.has_job = !this.filters.has_job
      this.runFilters()
    },

    isSelected(transfer) {
      return this.selectedItems.some(s => s.id === transfer.id && s.source === transfer.source)
    },

    toggleItem(transfer) {
      const idx = this.selectedItems.findIndex(s => s.id === transfer.id && s.source === transfer.source)
      if (idx === -1) {
        this.selectedItems.push({ id: transfer.id, source: transfer.source })
      } else {
        this.selectedItems.splice(idx, 1)
      }
    },

    toggleAll() {
      if (this.allPageSelected) {
        const pageKeys = new Set((this.transfers.data || []).map(t => `${t.source}:${t.id}`))
        this.selectedItems = this.selectedItems.filter(s => !pageKeys.has(`${s.source}:${s.id}`))
      } else {
        const selectedKeys = new Set(this.selectedItems.map(s => `${s.source}:${s.id}`))
        ;(this.transfers.data || []).forEach(t => {
          if (!selectedKeys.has(`${t.source}:${t.id}`)) {
            this.selectedItems.push({ id: t.id, source: t.source })
          }
        })
      }
    },

    clearSelection() {
      this.selectedItems = []
    },

    downloadCsv() {
      this.downloading = true
      let params
      if (this.selectedItems.length > 0) {
        params = {
          transaction_ids: this.selectedItems.filter(s => s.source === 'transaction').map(s => s.id),
          transfer_ids:    this.selectedItems.filter(s => s.source === 'transfer').map(s => s.id),
        }
      } else {
        const activeMulti = Object.fromEntries(
          Object.entries(this.multiFilters).filter(([, v]) => v.length > 0)
        )
        const cleaned = Object.fromEntries(
          Object.entries(this.filters).filter(([k, v]) => k !== 'page' && v !== null && v !== '' && v !== undefined && v !== false)
        )
        params = { ...cleaned, ...activeMulti }
      }
      backend.downloadCsv(
        params,
        response => {
          const contentType = response.headers['content-type'] || ''
          if (contentType.includes('application/json')) {
            response.data.text().then(text => {
              this.$toast.info(JSON.parse(text).message)
              this.downloading = false
            })
          } else {
            download(
              response.data,
              `asset-transfers-${moment().format('DD-MM-YYYY')}.csv`,
              'text/csv'
            )
            this.downloading = false
          }
        },
        () => {
          this.$whoops()
          this.downloading = false
        }
      )
    },
  },

  computed: {
    ...mapGetters('assetTransfer', ['transfers']),

    allPageSelected() {
      const page = this.transfers.data || []
      return page.length > 0 && page.every(t => this.selectedItems.some(s => s.id === t.id && s.source === t.source))
    },

    isIndeterminate() {
      const page = this.transfers.data || []
      return page.some(t => this.selectedItems.some(s => s.id === t.id && s.source === t.source)) && !this.allPageSelected
    },

    selectedCount() {
      return this.selectedItems.length
    },
  },

  watch: {
    '$route'(to) {
      const q = to.query
      this.filters = { asset_code: q.asset_code || '', asset_barcode: q.asset_barcode || '', from_date: q.from_date || '', to_date: q.to_date || '', has_job: q.has_job === 'true', page: q.page || '' }
      this.restoreMultiFilters(q)
      this.loadReport()
    },
    'multiFilters.asset_type_ids'(newIds, oldIds) {
      const newSingle = (newIds || []).length === 1 ? newIds[0] : null
      const oldSingle = (oldIds || []).length === 1 ? oldIds[0] : null
      if (newSingle !== null && oldSingle !== null && newSingle !== oldSingle) {
        this.multiFilters.dynamic_status_option_ids = []
      }
    },
  },

  beforeRouteLeave(to, from, next) {
    this.$store.commit('assetTransfer/clear')
    next()
  },

}
</script>
